import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { MapPin, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

export default function DirectoryState() {
  const { state } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/directory/${state}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [state]);

  useSeo({
    title: data ? `Dry ice blasting pros in ${data.state_name} · Dry Ice Connect` : "Dry Ice Connect",
    description: data ? `Verified dry ice blasting contractors across ${data.state_name}. Browse ${data.cities?.length || 0} cit${data.cities?.length === 1 ? "y" : "ies"} with mobile and in-shop pros for automotive, industrial, and restoration work.` : "",
    canonical: `${window.location.origin}/pros/${state}`,
  });

  if (notFound) return <Navigate to="/pros" replace />;
  if (!data) return <Layout><div className="max-w-3xl mx-auto px-4 py-24 font-mono text-xs text-[#52525B] text-center">[LOADING]</div></Layout>;

  return (
    <Layout>
      <section className="border-b border-[#262626] bg-grid bg-grid-fade">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14">
          <Link to="/pros" className="font-mono text-xs text-[#52525B] hover:text-[#00D4FF] inline-flex items-center gap-1 mb-4" data-testid="state-back">
            <ArrowLeft size={12} weight="bold" /> ALL STATES
          </Link>
          <div className="section-label mb-3">— {data.state}</div>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tighter" data-testid="state-headline">
            Dry ice blasting pros in <span className="text-[#00D4FF]">{data.state_name}</span>.
          </h1>
          <p className="font-plex text-base text-[#9CA3AF] mt-4 max-w-2xl">
            {data.cities.length} cit{data.cities.length === 1 ? "y" : "ies"} covered. Mobile and in-shop pros for automotive, industrial, marine and restoration work.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
        <div className="section-label mb-5">— CITIES</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="state-cities">
          {data.cities.map((c) => (
            <Link
              key={c.city_slug}
              to={`/pros/${data.state_slug}/${c.city_slug}`}
              className="surface surface-hover p-5 group flex items-center justify-between"
              data-testid={`state-city-${c.city_slug}`}
            >
              <div>
                <MapPin size={14} weight="duotone" className="text-[#00D4FF] mb-2" />
                <div className="font-chivo font-bold text-white">{c.city}, {data.state}</div>
                <div className="font-mono text-[11px] text-[#52525B] mt-1">{c.listing_count} pro{c.listing_count === 1 ? "" : "s"}</div>
              </div>
              <ArrowRight size={16} className="text-[#52525B] group-hover:text-[#00D4FF]" weight="bold" />
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
